import { isSuccessResult } from '.';
import { FailureResult, Result, SuccessResult } from './result';

export type KeyedSuccessResult<TKey, TSuccess> = SuccessResult<TSuccess> & { key: TKey };

export type KeyedFailureResult<TKey, TFailure = Error> = FailureResult<TFailure> & { key: TKey };

export type KeyedResult<TKey, TSuccess, TFailure = Error> =
    | KeyedFailureResult<TKey, TFailure>
    | KeyedSuccessResult<TKey, TSuccess>;

/**
 * Return a keyed success result.
 *
 * @param key - The key
 * @param value - The success value
 * @returns A success result
 */
export function keyedSuccessResult<TKey, TSuccess>(key: TKey, value: TSuccess): KeyedSuccessResult<TKey, TSuccess> {
    return {
        key,
        success: true,
        value,
    };
}

/**
 * Return a keyed failure result.
 *
 * @param key - The key
 * @param failure - The failure
 * @returns A failure result
 */
export function keyedFailureResult<TKey, TFailure>(key: TKey, failure: TFailure): KeyedFailureResult<TKey, TFailure> {
    return {
        failure,
        key,
        success: false,
    };
}

/**
 * Maps an array of results to the key by using the value as the key.
 *
 * @param resultMappingFunction - The result
 * @returns The keyed results
 */
export function mapToKeyedResult<TKey, TSuccess, TFailure>(
    resultMappingFunction: (value: TKey) => Result<TSuccess, TFailure>,
): (value: TKey) => KeyedResult<TKey, TSuccess, TFailure>;
/**
 * Maps an array of results to the key by the mapping function
 *
 * @param keyMappingFunction - The mapping function for the key
 * @param resultMappingFunction - The results mapping function
 * @returns The keyed results
 */
export function mapToKeyedResult<T, TKey, TSuccess, TFailure>(
    keyMappingFunction: (value: T) => TKey,
    resultMappingFunction: (value: T) => Result<TSuccess, TFailure>,
): (value: T) => KeyedResult<TKey, TSuccess, TFailure>;
/**
 * Maps an array of results to the key by the mapping function
 *
 * @param keyMappingFunction - The mapping function for the key or the result mapping function
 * @param resultMappingFunction - The result mapping function
 * @returns The keyed results
 */
export function mapToKeyedResult<TKey, TSuccess, TFailure>(
    keyMappingFunction: ((value: any) => Result<TSuccess, TFailure>) | ((value: any) => TKey),
    resultMappingFunction?: (value: any) => Result<TSuccess, TFailure>,
): (value: any) => KeyedResult<TKey, TSuccess, TFailure> {
    const typedKeyMappingFunc: (value: any) => TKey =
        resultMappingFunction === undefined ? key => key : keyMappingFunction;
    const typedResultMappingFunc: (value: any) => Result<TSuccess, TFailure> =
        resultMappingFunction === undefined
            ? (keyMappingFunction as (value: any) => Result<TSuccess, TFailure>)
            : resultMappingFunction;
    return value => {
        const key = typedKeyMappingFunc(value);
        const result = typedResultMappingFunc(value);
        if (isSuccessResult(result)) {
            return keyedSuccessResult(key, result.value);
        } else {
            return keyedFailureResult(key, result.failure);
        }
    };
}
